// SNIGJ - Printable Document Component with Logo and QR Code
import React, { useRef, forwardRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';

const PrintableDocument = forwardRef(({ 
  documentType = 'PERCEPTION_NOTE', // PERCEPTION_NOTE, INVOICE, RECEIPT, SUMMONS
  documentId,
  data,
  language = 'fr',
  onPrint
}, ref) => {
  const printRef = useRef();
  const baseUrl = window.location.origin;
  const documentUrl = `${baseUrl}/documents/verify/${documentId}`;
  
  const handlePrint = () => {
    const printContent = printRef.current;
    if (printContent) {
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>${getDocumentTitle()} - ${documentId}</title>
            <style>
              @page { margin: 20mm; size: A4; }
              body { 
                font-family: 'Times New Roman', serif; 
                padding: 0; 
                margin: 0;
                color: #000;
                line-height: 1.4;
              }
              .document-container {
                max-width: 210mm;
                margin: 0 auto;
                padding: 15mm;
              }
              .header { 
                text-align: center; 
                border-bottom: 3px double #000; 
                padding-bottom: 15px; 
                margin-bottom: 20px; 
              }
              .logo-container {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 20px;
                margin-bottom: 10px;
              }
              .logo { 
                width: 80px; 
                height: auto; 
              }
              .header-text h1 { 
                margin: 0; 
                font-size: 16px; 
                text-transform: uppercase;
                letter-spacing: 2px;
              }
              .header-text h2 { 
                margin: 5px 0; 
                font-size: 14px; 
                font-weight: normal; 
              }
              .header-text h3 { 
                margin: 10px 0 0; 
                font-size: 18px; 
                font-weight: bold;
                border: 1px solid #000;
                padding: 8px 20px;
                display: inline-block;
              }
              .details { margin: 25px 0; }
              .details table { 
                width: 100%; 
                border-collapse: collapse; 
              }
              .details td { 
                padding: 10px; 
                border-bottom: 1px solid #ddd; 
                vertical-align: top;
              }
              .details td:first-child { 
                font-weight: bold; 
                width: 35%; 
                background: #f9f9f9;
              }
              .amount-box { 
                text-align: center; 
                margin: 30px 0; 
                padding: 20px; 
                border: 3px solid #000;
                background: #f5f5f5;
              }
              .amount-box .label {
                font-size: 14px;
                text-transform: uppercase;
                margin-bottom: 10px;
              }
              .amount-box .value {
                font-size: 32px;
                font-weight: bold;
              }
              .status-badge { 
                display: inline-block; 
                padding: 8px 25px; 
                border-radius: 25px; 
                font-weight: bold;
                font-size: 14px;
                text-transform: uppercase;
              }
              .status-badge.paid { background: #d4edda; color: #155724; border: 2px solid #155724; }
              .status-badge.pending { background: #fff3cd; color: #856404; border: 2px solid #856404; }
              .status-badge.overdue { background: #f8d7da; color: #721c24; border: 2px solid #721c24; }
              .qr-section {
                display: flex;
                justify-content: space-between;
                align-items: flex-end;
                margin-top: 40px;
                padding-top: 20px;
                border-top: 1px solid #ddd;
              }
              .qr-code {
                text-align: center;
              }
              .qr-code p {
                font-size: 10px;
                color: #666;
                margin-top: 5px;
              }
              .signature-section {
                text-align: right;
              }
              .signature-line {
                width: 200px;
                border-bottom: 1px solid #000;
                margin: 40px 0 5px auto;
              }
              .signature-label {
                font-size: 12px;
              }
              .footer { 
                margin-top: 30px; 
                text-align: center; 
                font-size: 10px; 
                color: #666;
                border-top: 1px solid #ddd;
                padding-top: 15px;
              }
              @media print { 
                body { padding: 0; } 
                .document-container { padding: 0; }
              }
            </style>
          </head>
          <body>
            ${printContent.innerHTML}
          </body>
        </html>
      `);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    }
    if (onPrint) onPrint();
  };

  const getDocumentTitle = () => {
    const titles = {
      PERCEPTION_NOTE: language === 'fr' ? 'NOTE DE PERCEPTION' : 'FEE NOTE',
      INVOICE: language === 'fr' ? 'FACTURE' : 'INVOICE',
      RECEIPT: language === 'fr' ? 'REÇU DE PAIEMENT' : 'PAYMENT RECEIPT',
      SUMMONS: language === 'fr' ? 'CONVOCATION' : 'SUMMONS'
    };
    return titles[documentType] || titles.PERCEPTION_NOTE;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const renderPerceptionNote = () => (
    <>
      <div className="details">
        <table>
          <tbody>
            <tr>
              <td>{language === 'fr' ? 'Numéro de référence' : 'Reference Number'}</td>
              <td style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{data.fee_id || documentId}</td>
            </tr>
            <tr>
              <td>{language === 'fr' ? 'Date d\'émission' : 'Issue Date'}</td>
              <td>{formatDate(data.created_at || new Date().toISOString())}</td>
            </tr>
            <tr>
              <td>{language === 'fr' ? 'Bénéficiaire / Débiteur' : 'Beneficiary / Debtor'}</td>
              <td>{data.citizen_name || data.payer_name || '—'}</td>
            </tr>
            <tr>
              <td>{language === 'fr' ? 'Identité Nationale' : 'National ID'}</td>
              <td>{data.national_id || '—'}</td>
            </tr>
            <tr>
              <td>{language === 'fr' ? 'Objet / Motif' : 'Subject / Reason'}</td>
              <td>{data.description || '—'}</td>
            </tr>
            <tr>
              <td>{language === 'fr' ? 'Type de frais' : 'Fee Type'}</td>
              <td>{(data.fee_type || 'COURT_FEE').replace(/_/g, ' ')}</td>
            </tr>
            <tr>
              <td>{language === 'fr' ? 'Date d\'échéance' : 'Due Date'}</td>
              <td style={{ color: data.status === 'OVERDUE' ? '#dc3545' : 'inherit', fontWeight: 'bold' }}>
                {formatDate(data.due_date)}
              </td>
            </tr>
            {data.case_id && (
              <tr>
                <td>{language === 'fr' ? 'Dossier associé' : 'Related Case'}</td>
                <td style={{ fontFamily: 'monospace' }}>{data.case_id}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="amount-box">
        <div className="label">{language === 'fr' ? 'Montant à payer' : 'Amount Due'}</div>
        <div className="value">{data.amount?.toFixed(2)} {data.currency || 'USD'}</div>
      </div>

      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
        <span className={`status-badge ${data.status?.toLowerCase()}`}>
          {data.status === 'PAID' ? (language === 'fr' ? '✓ PAYÉ' : '✓ PAID') : 
           data.status === 'OVERDUE' ? (language === 'fr' ? '⚠ EN RETARD' : '⚠ OVERDUE') :
           (language === 'fr' ? 'EN ATTENTE DE PAIEMENT' : 'PAYMENT PENDING')}
        </span>
      </div>

      {data.status === 'PAID' && data.paid_at && (
        <div style={{ textAlign: 'center', color: '#155724', marginBottom: '20px' }}>
          {language === 'fr' ? 'Paiement effectué le' : 'Payment received on'}: {formatDate(data.paid_at)}
        </div>
      )}
    </>
  );

  const renderSummons = () => (
    <>
      <div className="details">
        <table>
          <tbody>
            <tr>
              <td>{language === 'fr' ? 'Destinataire' : 'Recipient'}</td>
              <td style={{ fontWeight: 'bold' }}>{data.recipient_name || '—'}</td>
            </tr>
            <tr>
              <td>{language === 'fr' ? 'Objet' : 'Subject'}</td>
              <td>{data.title || '—'}</td>
            </tr>
            <tr>
              <td>{language === 'fr' ? 'Message' : 'Message'}</td>
              <td>{data.message || '—'}</td>
            </tr>
            {data.scheduled_date && (
              <tr>
                <td>{language === 'fr' ? 'Date de comparution' : 'Appearance Date'}</td>
                <td style={{ fontWeight: 'bold', color: '#dc3545' }}>
                  {formatDate(data.scheduled_date)}
                  {data.scheduled_time && ` à ${data.scheduled_time}`}
                </td>
              </tr>
            )}
            {data.location && (
              <tr>
                <td>{language === 'fr' ? 'Lieu' : 'Location'}</td>
                <td>{data.location}</td>
              </tr>
            )}
            {data.case_id && (
              <tr>
                <td>{language === 'fr' ? 'Dossier' : 'Case'}</td>
                <td style={{ fontFamily: 'monospace' }}>{data.case_id}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {data.is_urgent && (
        <div style={{ textAlign: 'center', margin: '20px 0' }}>
          <span className="status-badge overdue">
            {language === 'fr' ? '⚠ URGENT' : '⚠ URGENT'}
          </span>
        </div>
      )}
    </>
  );

  return (
    <div>
      <div ref={printRef} style={{ display: 'none' }}>
        <div className="document-container">
          {/* Header with Logo */}
          <div className="header">
            <div className="logo-container">
              <img src="/logo-justice.png" alt="Logo" className="logo" />
              <div className="header-text">
                <h1>RÉPUBLIQUE DÉMOCRATIQUE DU CONGO</h1>
                <h2>MINISTÈRE DE LA JUSTICE ET GARDE DES SCEAUX</h2>
                <h3>{getDocumentTitle()} N° {(data.fee_id || documentId)?.slice(-8)?.toUpperCase()}</h3>
              </div>
              <img src="/logo-justice.png" alt="Logo" className="logo" />
            </div>
          </div>

          {/* Content based on document type */}
          {documentType === 'SUMMONS' ? renderSummons() : renderPerceptionNote()}

          {/* QR Code and Signature Section */}
          <div className="qr-section">
            <div className="qr-code">
              <QRCodeSVG 
                value={documentUrl} 
                size={80}
                level="M"
                includeMargin={true}
              />
              <p>{language === 'fr' ? 'Scannez pour vérifier' : 'Scan to verify'}</p>
            </div>
            <div className="signature-section">
              <div className="signature-line"></div>
              <div className="signature-label">
                {language === 'fr' ? 'Signature et cachet' : 'Signature and stamp'}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="footer">
            <p><strong>SNIGJ</strong> - Système National Intégré de Gestion de la Justice</p>
            <p>{language === 'fr' 
              ? 'Document officiel généré électroniquement - Vérifiable via le QR code ci-dessus' 
              : 'Official document generated electronically - Verifiable via QR code above'}</p>
            <p style={{ marginTop: '10px', fontFamily: 'monospace' }}>
              ID: {documentId} | {new Date().toISOString()}
            </p>
          </div>
        </div>
      </div>

      {/* Visible Preview */}
      <div ref={ref} className="p-6 bg-white dark:bg-slate-900 rounded border">
        {/* Preview Header */}
        <div className="text-center border-b-2 border-black dark:border-white pb-4 mb-4">
          <div className="flex items-center justify-center gap-4 mb-3">
            <img src="/logo-justice.png" alt="Logo" className="w-16 h-16 object-contain" />
            <div>
              <h1 className="text-lg font-bold">RÉPUBLIQUE DÉMOCRATIQUE DU CONGO</h1>
              <h2 className="text-sm">MINISTÈRE DE LA JUSTICE ET GARDE DES SCEAUX</h2>
            </div>
            <img src="/logo-justice.png" alt="Logo" className="w-16 h-16 object-contain" />
          </div>
          <h3 className="text-base font-bold mt-2 border border-black dark:border-white inline-block px-4 py-2">
            {getDocumentTitle()} N° {(data.fee_id || documentId)?.slice(-8)?.toUpperCase()}
          </h3>
        </div>

        {/* Preview Content */}
        {documentType === 'SUMMONS' ? renderSummons() : renderPerceptionNote()}

        {/* QR Code Section */}
        <div className="flex justify-between items-end mt-8 pt-4 border-t">
          <div className="text-center">
            <QRCodeSVG 
              value={documentUrl} 
              size={80}
              level="M"
              includeMargin={true}
              className="mx-auto"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {language === 'fr' ? 'Scannez pour vérifier' : 'Scan to verify'}
            </p>
          </div>
          <div className="text-right">
            <div className="w-40 border-b border-black dark:border-white mb-1"></div>
            <p className="text-xs">{language === 'fr' ? 'Signature et cachet' : 'Signature and stamp'}</p>
          </div>
        </div>

        {/* Preview Footer */}
        <div className="text-center text-xs text-muted-foreground mt-6 pt-4 border-t">
          <p><strong>SNIGJ</strong> - Système National Intégré de Gestion de la Justice</p>
          <p className="mt-1 font-mono">ID: {documentId}</p>
        </div>
      </div>

      {/* Print Button Hook */}
      <button 
        onClick={handlePrint}
        style={{ display: 'none' }}
        data-print-trigger="true"
      />
    </div>
  );
});

PrintableDocument.displayName = 'PrintableDocument';

export default PrintableDocument;
